import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { AiBlockPanel } from './AiBlockPanel';
import {
  AiFocusPlaceholder,
  getPlaceholderName,
  type PlaceholderDashlet,
} from './AiFocusPlaceholder';
import { AiFocusSlot } from './AiFocusSlot';
import type { AiBlockPanelContext } from './connectOptions';
import type { DashletWithAiBlock } from './aiBlockMarker';
import {
  CLOSED_SESSION,
  aiBlockSessionReducer,
  buildAiPanelContext,
  buildAiPanelProps,
  buildOpenEditAction,
  getFocusedDashletId,
  getSessionDashletId,
  reconcileSessionWithPage,
  resolveVisibleSession,
} from './aiBlockSession';
import {
  AI_MOTION_MS,
  AI_MOTION_SETTLE_SLACK_MS,
  usePrefersReducedMotion,
} from 'ts-components/PiChat/motion';
import {
  IDLE_FOCUS,
  nextFocusView,
  settleFocusView,
  settleFocusViewFor,
  shouldShowLive,
  shouldShowPlaceholder,
  type FocusPhase,
} from './motion';
import { useKiCodeAccess } from './useKiCodeAccess';

type EditableDashlet = DashletWithAiBlock & { id: string };

export interface UseAiBlockSessionOptions {
  targetType: AiBlockPanelContext['targetType'];
  dashboardId?: string | null;
  objectId?: string | null; // chart groups only
  dashlets?: EditableDashlet[] | null;
  dashboardName?: string | null;
  objectName?: string;
}

export const useAiBlockSession = ({
  targetType,
  dashboardId,
  objectId,
  dashlets,
  dashboardName,
  objectName,
}: UseAiBlockSessionOptions) => {
  const [rawState, dispatch] = useReducer(
    aiBlockSessionReducer,
    CLOSED_SESSION
  );
  const sessionCounterRef = useRef(0);
  // Without KiCode access no session can start: create no-ops and edit
  // returns false so callers fall back to the standard block editor.
  const canAccessKiCode = useKiCodeAccess();

  const endAction = reconcileSessionWithPage(rawState, dashboardId);
  if (endAction) dispatch(endAction);
  const state = resolveVisibleSession(rawState, dashboardId);
  const sessionDashletId = getSessionDashletId(state);
  const sessionConversationId =
    state.status === 'open' ? state.conversationId : undefined;
  const focusedDashletId = getFocusedDashletId(state) ?? null;

  const reducedMotion = usePrefersReducedMotion();
  const [focusView, setFocusView] = useState(IDLE_FOCUS);
  const nextView = nextFocusView(focusView, focusedDashletId, reducedMotion);
  if (nextView !== focusView) setFocusView(nextView);

  const crossfading =
    focusView.phase === 'entering' || focusView.phase === 'exiting';
  useEffect(() => {
    if (!crossfading) return undefined;
    const timer = setTimeout(
      () => setFocusView(settleFocusView),
      AI_MOTION_MS + AI_MOTION_SETTLE_SLACK_MS
    );
    return () => clearTimeout(timer);
  }, [crossfading, focusView.phase, focusView.id]);

  const onPlaceholderSettled = useCallback(
    (phase: FocusPhase) =>
      setFocusView((view) => settleFocusViewFor(view, phase)),
    []
  );
  const renderFocusSlot = useCallback(
    (
      dashlet: PlaceholderDashlet,
      isDraggable: boolean,
      renderLive: () => ReactNode
    ) => (
      <AiFocusSlot
        placeholder={
          shouldShowPlaceholder(focusView, dashlet.id) ? (
            <AiFocusPlaceholder
              dashlet={dashlet}
              isDraggable={isDraggable}
              phase={focusView.phase}
              onSettled={onPlaceholderSettled}
            />
          ) : null
        }
      >
        {shouldShowLive(focusView, dashlet.id) ? renderLive() : null}
      </AiFocusSlot>
    ),
    [focusView, onPlaceholderSettled]
  );

  const openCreate = useCallback(() => {
    if (!canAccessKiCode || !dashboardId) return;
    sessionCounterRef.current += 1;
    dispatch({
      type: 'open_create',
      sessionKey: `ai-block-session-${sessionCounterRef.current}`,
      context: buildAiPanelContext(targetType, dashboardId, objectId),
    });
  }, [canAccessKiCode, targetType, dashboardId, objectId]);

  const openEdit = useCallback(
    (dashlet: EditableDashlet): boolean => {
      if (!canAccessKiCode || !dashboardId) return false;
      const action = buildOpenEditAction(
        dashlet,
        buildAiPanelContext(targetType, dashboardId, objectId),
        `ai-block-session-${sessionCounterRef.current + 1}`,
        { dashletId: sessionDashletId, conversationId: sessionConversationId }
      );
      if (!action) return false;
      sessionCounterRef.current += 1;
      dispatch(action);
      return true;
    },
    [
      canAccessKiCode,
      targetType,
      dashboardId,
      objectId,
      sessionDashletId,
      sessionConversationId,
    ]
  );

  const close = useCallback(() => dispatch({ type: 'close' }), []);

  const sessionDashlet = sessionDashletId
    ? dashlets?.find((d) => d.id === sessionDashletId)
    : undefined;
  const panelProps = useMemo(() => buildAiPanelProps(state), [state]);
  const blockName = sessionDashlet
    ? getPlaceholderName(sessionDashlet as unknown as PlaceholderDashlet)
    : null;
  const panelKey = panelProps?.key;
  const panelContext = panelProps?.context;
  const panelDashletId = panelProps?.dashletId;
  const panelConversationId = panelProps?.conversationId;

  const onBlockPushed = useMemo(
    () =>
      panelKey
        ? (dashletId: string, conversationId: string) =>
            dispatch({
              type: 'block_pushed',
              sessionKey: panelKey,
              dashletId,
              conversationId,
            })
        : undefined,
    [panelKey]
  );

  const panel = useMemo(
    () =>
      panelKey && panelContext ? (
        <AiBlockPanel
          key={panelKey}
          context={panelContext}
          dashletId={panelDashletId}
          conversationId={panelConversationId}
          onClose={close}
          onBlockPushed={onBlockPushed}
          blockName={blockName}
          dashboardName={dashboardName}
          objectName={objectName}
        />
      ) : null,
    [
      panelKey,
      panelContext,
      panelDashletId,
      panelConversationId,
      close,
      onBlockPushed,
      blockName,
      dashboardName,
      objectName,
    ]
  );

  return {
    state,
    isOpen: state.status === 'open',
    focusedDashletId,
    renderFocusSlot,
    sessionDashletId,
    openCreate,
    openEdit,
    close,
    panelProps,
    panel,
  };
};
